import { useEffect, useRef, useState } from "react";
import { Estado, Municipio } from "./estadoMunType";
import { Paginated, RequestError, useApi } from "../../services";
import {
  Box,
  Divider,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { ErrorModal, LoadingModal } from "../modals";
import { useWindowSize } from "../../core";
import { Search } from "@mui/icons-material";
import { ChooseEstado } from "./chooseEstado";

interface TableMunicipioProps {
  onChoose?: (municipio: Municipio) => void;
}

export const TableMunicipio: React.FC<TableMunicipioProps> = ({ onChoose }) => {
  const api = useApi();
  const { height, width } = useWindowSize();

  const divRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const changeTimer = useRef<NodeJS.Timeout | null>(null);

  const [size, setSize] = useState({ width: 0, height: 0 });
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [filterSize, setFilterSize] = useState({ width: 0, height: 0 });
  const [titleSize, setTitleSize] = useState({ width: 0, height: 0 });

  const [name, setName] = useState<string | undefined>();
  const [estado, setEstado] = useState<Estado | undefined>();
  const [paginate, setPaginate] = useState<Paginated<Municipio> | RequestError | undefined>();
  const [pageOpts, setPageOpts] = useState<{ page: number; perPage: number; }>({ page: 0, perPage: 10 });


  useEffect(() => {
    if (divRef.current) {
      const { width, height } = divRef.current.getBoundingClientRect();
      setSize({ width, height });
    }
    if (pageRef.current) {
      const { width, height } = pageRef.current.getBoundingClientRect();
      setPageSize({ width, height });
    }
    if (filterRef.current) {
      const { width, height } = filterRef.current.getBoundingClientRect();
      setFilterSize({ width, height });
    }
    if (titleRef.current) {
      const { width, height } = titleRef.current.getBoundingClientRect();
      setTitleSize({ width, height });
    }
  }, [divRef, pageRef, filterRef, titleRef, height, width, paginate]);

  useEffect(() => {
    if (changeTimer.current) clearTimeout(changeTimer.current);
    changeTimer.current = setTimeout(() => {
      api.estadosMunicipios.getMunicipios(pageOpts.page, pageOpts.perPage, name, estado?.id).then(setPaginate);
    }, 500);

    return () => {
      if (changeTimer.current) clearTimeout(changeTimer.current);
    };
  }, [name, estado, pageOpts]);


  if (!paginate) return <LoadingModal open />;
  if ("error" in paginate) return <ErrorModal error={paginate.error} message={paginate.message} />;

  return <Paper
    ref={divRef}
    sx={{
      width: "100%",
      height: paginate && !("error" in paginate) ? "100%" : "196px",
      display: "flex",
      flexDirection: "column",
      alignItems: paginate && !("error" in paginate) ? "start" : "center",
      justifyContent: paginate && !("error" in paginate) ? "start" : "center",
    }}
  >

    <>
      {/* Título */}
      <Box
        ref={titleRef}
        sx={{
          paddingTop: "7px",
          paddingX: "15px",
        }}>
        <Typography fontSize={18} fontWeight={"bold"}>
          {"Municípios"}
        </Typography>
      </Box>

      {/* Filtros por nome e estado*/}
      <div
        ref={filterRef}
        style={{ width: "100%" }}
      >
        <Box sx={{
          flex: 1,
          gap: "15px",
          display: "flex",
          paddingX: "15px",
          flexDirection: "row",
          justifyContent: "space-between",
        }}>

          {/* Filtro pelo nome */}
          <TextField
            fullWidth
            label="Nome do Município"
            name="nome"
            value={name}
            onChange={(event) => {
              setPageOpts({
                page: 0,
                perPage: paginate.perPage,
              });
              setName(event.target.value);
            }}
            variant="standard"
            InputProps={{
              endAdornment: <InputAdornment position="end">
                <Search />
              </InputAdornment>,
            }}
          />

          {/* Filtro pelo Estado */}
          <Box sx={{ width: "25%" }}>
            <ChooseEstado
              removeOption
              initialEstado={estado}
              onChoose={(estado) => {
                setPageOpts({
                  page: 0,
                  perPage: paginate.perPage,
                });
                setEstado(estado);
              }}
            />
          </Box>

        </Box>

        <div style={{ height: "15px" }} />
        <Divider />
        <div style={{ height: "15px" }} />
      </div>

      {/* Tabela de estações */}
      <TableContainer sx={{
        flex: 1,
        overflow: "auto",
        height: size.height - pageSize.height - filterSize.height - titleSize.height,
        width: size.width
      }}>
        <Table
          stickyHeader
          aria-label="sticky table"
          size="small"
        >
          <TableHead>
            <TableRow>
              <TableCell align="center">Nome</TableCell>
              <TableCell align="center">Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginate.items.map((row) => (
              <TableRow
                hover
                key={row.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                onClick={() => onChoose && onChoose(row)}
              >
                <TableCell align="justify" sx={{ textOverflow: "ellipsis" }} >{row.nome}</TableCell>
                <TableCell align="center">{row.microrregiao.mesorregiao.UF.nome}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <TablePagination
        ref={pageRef}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={paginate.totalItems}
        rowsPerPage={paginate.perPage}
        page={paginate.page}
        onPageChange={(_, page) => { setPageOpts({ ...paginate, page: page }); }}
        onRowsPerPageChange={(event) => { setPageOpts({ ...paginate, perPage: parseInt(event.target.value, 10) }); }}
      />
    </>
  </Paper>;
};